import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import {
	Box,
	Card,
	CardContent,
	Checkbox,
	Grid2,
	IconButton,
	Link,
	Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { FreelancerOnboardingMyPresentationExperienceViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import { zoullooColors } from "../../../../../theme";
import { calculateDuration, formatDateForDisplay } from "../../../../../utils/dateUtils";
import HtmlEditor from "../../../../UI/HtmlEditor";
import { useEntityTranslations } from "../../../../../hooks/useTranslations";
import { useLanguage } from "../../../../../i18n/LanguageContext";
import { LanguageCode } from "../../../../../models/enums/LanguageCode";
import TranslatableFieldWrapper from "../../../../translation/TranslatableFieldWrapper";

interface ExperienceDisplayProps {
	readonly experience: FreelancerOnboardingMyPresentationExperienceViewModel;
	readonly index: number;
	readonly onEdit: (index: number) => void;
	readonly onDelete: (index: number) => void;
	readonly expandedDescriptions: { [key: number]: boolean };
	readonly descriptionOverflows: { [key: number]: boolean };
	readonly onToggleDescription: (index: number) => void;
	readonly setDescriptionRef: (index: number, el: HTMLElement | null) => void;
}

export default function ExperienceDisplay({
	experience,
	index,
	onEdit,
	onDelete,
	expandedDescriptions,
	descriptionOverflows,
	onToggleDescription,
	setDescriptionRef
}: ExperienceDisplayProps) {
	const { t, i18n } = useTranslation();
	const { currentLanguage } = useLanguage();
	const [viewingLanguage, setViewingLanguage] = useState<LanguageCode>(currentLanguage);

	const { data: expTranslations } = useEntityTranslations(
		"FreelancerProfileProfessionalExperience",
		experience.id,
		viewingLanguage.toLowerCase()
	);

	const getField = (fieldName: string, frValue?: string | null): string => {
		if (viewingLanguage === LanguageCode.Fr) return frValue || "";
		const pascalField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
		return expTranslations?.[pascalField] || frValue || "";
	};

	const handleEdit = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onEdit(index);
		},
		[onEdit, index]
	);

	const handleDelete = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onDelete(index);
		},
		[onDelete, index]
	);

	const handleToggleDescription = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onToggleDescription(index);
		},
		[onToggleDescription, index]
	);

	const handleSetDescriptionRef = useCallback(
		(el: HTMLElement | null) => {
			setDescriptionRef(index, el);
		},
		[setDescriptionRef, index]
	);

	const startDateFormatted = formatDateForDisplay(experience.startDate, i18n.language);
	const endDateFormatted = experience.isCurrentPosition
		? t("freelancerOnboardingMyPresentation.display.today")
		: formatDateForDisplay(experience.endDate, i18n.language);
	const duration = calculateDuration(
		experience.startDate,
		experience.endDate,
		experience.isCurrentPosition,
		t,
		"freelancerOnboardingMyPresentation.display.duration"
	);
	const isExpanded = expandedDescriptions[index] || false;

	return (
		<Card
			key={experience.id ?? index}
			sx={{
				marginBottom: 3
			}}
		>
			<CardContent sx={{ padding: 3 }}>
				<Box display="flex" gap={2}>
					{/* Company Logo Placeholder */}
					<Box
						sx={{
							width: 64,
							height: 64,
							backgroundColor: "#f8f9fa",
							borderRadius: "8px",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							border: "2px solid #e9ecef",
							flexShrink: 0
						}}
					>
						<Typography color="text.disabled" variant="body2">
							LOGO
						</Typography>
					</Box>

					{/* Main Content */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Grid2
							container
							spacing={2}
							alignItems="center"
							justifyContent="space-between"
						>
							<Grid2 size={{ xs: 12, md: 6 }} alignSelf={"center"} marginBottom={3}>
								{/* Header with Company Name and Star */}
								<Box display="flex" alignItems="center" gap={0.5}>
									<Typography variant="h5" color="text.primary">
										{experience.companyName}
									</Typography>
									<Checkbox
										icon={<StarOutlineIcon />}
										checkedIcon={<StarIcon color="primary" />}
										disabled={true}
										checked={experience.isTopExperience}
										sx={{ padding: 0, paddingBottom: 0.55 }}
									/>
								</Box>

								{/* Job Title and Job Type */}
								<Typography
									variant="body1"
									color="text.primary"
									fontWeight="medium"
									mb={0.5}
								>
									{getField("jobTitle", experience.jobTitle)}
									{experience.jobType &&
										` - ${t("JobTypes." + experience.jobType.label)}`}
								</Typography>
							</Grid2>
							<Grid2
								size={{ xs: 12, md: 6 }}
								position="relative"
								top={-25}
								right={-14}
								gap={0.5}
							>
								{/* Action Icons + Language Switch */}
								<Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
									<TranslatableFieldWrapper
										label=""
										editingLanguage={viewingLanguage}
										onLanguageChange={setViewingLanguage}
									>
										<></>
									</TranslatableFieldWrapper>
									<IconButton onClick={handleEdit} color="secondary" size="medium">
										<EditIcon fontSize="medium" />
									</IconButton>
									<IconButton onClick={handleDelete} color="default" size="medium">
										<DeleteIcon fontSize="medium" />
									</IconButton>
								</Box>
								{/* Right Side - Dates and Location + Actions */}
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-end",
										gap: 1,
										marginRight: 1
									}}
									color={zoullooColors.grey[550]}
								>
									{/* Date Range and Duration */}
									<Box textAlign="right">
										{(experience.startDate || experience.endDate) && (
											<Typography>
												{startDateFormatted}{" "}
												{t("freelancerOnboardingMyPresentation.display.to")}{" "}
												{endDateFormatted}
												{duration && ` - ${duration}`}
											</Typography>
										)}

										{/* Location */}
										{experience.city && <Typography>{experience.city.label}</Typography>}
									</Box>
								</Box>
							</Grid2>
						</Grid2>
						{/* Description */}
						{(experience.missionDescription || getField("missionDescription", experience.missionDescription)) && (
							<HtmlEditor
								ref={handleSetDescriptionRef}
								readonly={true}
								content={getField("missionDescription", experience.missionDescription)}
								sx={{
									display: "-webkit-box",
									WebkitLineClamp: isExpanded ? "none" : 3,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									textOverflow: "ellipsis"
								}}
							/>
						)}

						{/* Read More Link - only show if content overflows */}
						{experience.missionDescription && descriptionOverflows[index] && (
							<Link
								component="button"
								color="primary"
								onClick={handleToggleDescription}
								sx={{
									textDecoration: "none",
									fontWeight: "medium",
									cursor: "pointer",
									border: "none",
									background: "none",
									padding: 0,
									"&:hover": {
										textDecoration: "underline"
									}
								}}
							>
								{isExpanded
									? t("freelancerOnboardingMyPresentation.display.readLess")
									: t("freelancerOnboardingMyPresentation.display.readMore")}
							</Link>
						)}
					</Box>
				</Box>
			</CardContent>
		</Card>
	);
}
